import { DefaultLocale } from '../providers/tolgee';

export type TolgeeSSRInput = {
  language?: string;
  staticData?: Record<string, unknown>;
};

/**
 * Builds the `ssr` prop for `<TolgeeProvider>`.
 *
 * Always returns an object so Tolgee can hydrate correctly on the client.
 *
 * When the requested language is a non-default locale with no matching
 * `staticData` key, the `language` field is omitted from the returned config.
 * Tolgee then falls back to `defaultLanguage` (en-US) for the SSR render,
 * which we always have bundled. This avoids two broken alternatives:
 *
 * - Passing `language: 'en-GB'` with no en-GB data → Tolgee SSRs with empty
 *   strings, BackendFetch loads en-GB on the client → hydration mismatch →
 *   layout shift that scrolls the page.
 * - Passing `ssr={undefined}` → Tolgee skips SSR entirely, server renders with
 *   no translations, client loads them → same layout shift problem.
 */
export function buildTolgeeSSRProp(
  tolgeeSSR: TolgeeSSRInput | undefined,
  defaultLocaleData: Record<string, unknown>,
): TolgeeSSRInput {
  const language = tolgeeSSR?.language;
  const incomingStaticData = tolgeeSSR?.staticData;

  const staticData = {
    ...(incomingStaticData || {}),
    [DefaultLocale]: defaultLocaleData,
  };

  // Non-default locale with no matching static data: omit `language` so
  // Tolgee SSRs with the default locale instead of an unsupported one.
  if (language && language !== DefaultLocale && !incomingStaticData?.[language]) {
    return { staticData };
  }

  return {
    ...(tolgeeSSR || {}),
    staticData,
  };
}
